import React, { useState, useEffect } from 'react';
import { siteConfigApi } from '../../utils/api';
import { SiteConfig } from '../../types';
import toast from 'react-hot-toast';

const AdminSiteConfig: React.FC = () => {
  const [configs, setConfigs] = useState<Record<string, SiteConfig[]>>({});
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<SiteConfig | null>(null);
  const [editValue, setEditValue] = useState('');

  const loadConfigs = async () => {
    try {
      const response = await siteConfigApi.getAllConfigs();
      if (response.success && response.data) {
        setConfigs(response.data);
      }
    } catch (error) {
      toast.error('加载配置失败');
      console.error('Failed to load configs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleEditClick = (config: SiteConfig) => {
    setEditingConfig(config);
    setEditValue(config.config_value);
  };

  const handleSaveEdit = async () => {
    if (!editingConfig) return;
    
    try {
      const response = await siteConfigApi.updateConfig(editingConfig.config_key, editValue);
      if (response.success) {
        toast.success('配置更新成功');
        setEditingConfig(null);
        loadConfigs();
      }
    } catch (error) {
      toast.error('配置更新失败');
      console.error('Failed to update config:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
    setEditValue('');
  };

  const renderConfigInput = (config: SiteConfig) => {
    switch (config.config_type) {
      case 'boolean':
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
          >
            <option value="true">是</option>
            <option value="false">否</option>
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        );
      case 'json':
      case 'textarea':
        return (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 font-mono text-sm"
          />
        );
      default:
        return (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">站点配置</h1>
      </div>

      <div className="space-y-6">
        {Object.entries(configs).map(([groupName, groupConfigs]) => (
          <div key={groupName} className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {groupName === 'basic' ? '基本设置' : 
               groupName === 'content' ? '内容设置' : 
               groupName}
            </h2>
            <div className="space-y-4">
              {groupConfigs.map((config) => (
                <div key={config.id} className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{config.description || config.config_key}</span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {config.config_type}
                      </span>
                    </div>
                    {editingConfig?.id === config.id ? (
                      <div className="mt-2 space-y-2">
                        {renderConfigInput(config)}
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 bg-pink-600 text-white rounded hover:bg-pink-700 text-sm"
                          >
                            保存
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1">
                        <span className="text-gray-600 text-sm">
                          {config.config_value || '(空)'}
                        </span>
                      </div>
                    )}
                  </div>
                  {config.is_editable && editingConfig?.id !== config.id && (
                    <button
                      onClick={() => handleEditClick(config)}
                      className="text-pink-600 hover:text-pink-800 text-sm font-medium"
                    >
                      编辑
                    </button>
                  )}
                  {!config.is_editable && (
                    <span className="text-gray-400 text-sm">不可编辑</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSiteConfig;